import React, { useEffect, useState, useCallback } from 'react';
import { b2bAPI, userAPI, formatApiErrorDetail, exportAPI, downloadBlob } from '../api';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Download } from 'lucide-react';

const money = (v) => (v == null ? '\u2014' : `\u20b9${Number(v).toLocaleString('en-IN')}`);
const EMPTY = { company_name: '', contact_person: '', mobile: '', alternate_mobile: '', gstin: '', email: '', city: '', address: '', partner_type: '', assigned_to: '', status: 'active', notes: '' };

export const B2BPartnersPage = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission && hasPermission('b2b.manage');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [execs, setExecs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [chartRange, setChartRange] = useState('month');

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await b2bAPI.list(); setPartners(res.data.partners || []); }
    catch (e) { toast.error('Failed to load B2B partners'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); if (canManage) { userAPI.getAll().then(r => setExecs((r.data || []).filter(u => ['sales_executive', 'manager'].includes(u.role)))).catch(() => {}); } }, [load, canManage]);

  const save = async () => {
    if (!form.company_name.trim() || !form.mobile.trim()) { toast.error('Company name and mobile are required'); return; }
    try {
      if (editingId) { await b2bAPI.update(editingId, form); toast.success('B2B partner updated'); }
      else { await b2bAPI.create(form); toast.success('B2B partner created'); }
      setShowForm(false); setEditingId(null); setForm(EMPTY); load();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail, 'Failed to save partner')); }
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ company_name: p.company_name || '', contact_person: p.contact_person || '', mobile: p.mobile || '',
      alternate_mobile: p.alternate_mobile || '', gstin: p.gstin || '',
      email: p.email || '', city: p.city || '', address: p.address || '', partner_type: p.partner_type || '',
      assigned_to: p.assigned_to || '', status: p.status || 'active', notes: p.notes || '' });
    setDetail(null); setShowForm(true);
  };

  const openDetail = async (id) => {
    try { const res = await b2bAPI.get(id); setDetail(res.data); }
    catch (e) { toast.error('Failed to load partner'); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6" data-testid="b2b-partners-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-outfit font-semibold">B2B Partners</h1>
          <p className="text-muted-foreground mt-1">Partner/company accounts — multiple projects per partner count normally.</p>
        </div>
        <div className="flex gap-2">
          {hasPermission && hasPermission('report.export') && (
            <button onClick={async () => { try { const res = await exportAPI.download('b2b', { fmt: 'xlsx' }); downloadBlob(res, 'b2b-partners.xlsx'); toast.success('Export ready'); } catch (e) { toast.error('Export failed'); } }}
              data-testid="b2b-export" className="inline-flex items-center gap-1 px-3 py-2 bg-primary/10 text-primary rounded-lg text-sm"><Download className="h-4 w-4" /> Export</button>
          )}
          {canManage && <button onClick={() => { setEditingId(null); setForm(EMPTY); setShowForm(true); }} data-testid="b2b-add" className="px-4 py-2 bg-primary text-white rounded-lg">Add Partner</button>}
        </div>
      </div>

      <div className="clay-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/50 text-left"><tr>
            {['Company', 'Contact', 'Mobile', 'City', 'Relationship Mgr', 'Projects', 'Business', 'Outstanding', 'Status'].map(h => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {partners.length === 0 ? <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No partners yet.</td></tr> :
              partners.map(p => (
                <tr key={p.id} className="border-t border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40" onClick={() => openDetail(p.id)} data-testid="b2b-row">
                  <td className="px-3 py-2 font-medium text-primary">{p.company_name}</td>
                  <td className="px-3 py-2">{p.contact_person || '\u2014'}</td>
                  <td className="px-3 py-2">{p.mobile}</td>
                  <td className="px-3 py-2">{p.city || '\u2014'}</td>
                  <td className="px-3 py-2">{p.assigned_to_name || '\u2014'}</td>
                  <td className="px-3 py-2">{p.total_projects}</td>
                  <td className="px-3 py-2">{money(p.total_business)}</td>
                  <td className={`px-3 py-2 ${p.outstanding > 0 ? 'text-orange-600' : ''}`}>{money(p.outstanding)}</td>
                  <td className="px-3 py-2 capitalize">{p.status}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="clay-card p-6 w-full max-w-lg space-y-3" onClick={e => e.stopPropagation()} data-testid="b2b-form">
            <h3 className="text-lg font-medium">{editingId ? 'Edit B2B Partner' : 'Add B2B Partner'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Company Name *" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} data-testid="b2b-company" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm col-span-2" />
              <input placeholder="Contact Person" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
              <input placeholder="Mobile *" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="b2b-mobile" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
              <input placeholder="Alternate Mobile" value={form.alternate_mobile} onChange={e => setForm({ ...form, alternate_mobile: e.target.value })} data-testid="b2b-altmobile" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
              <input placeholder="GSTIN" value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value })} data-testid="b2b-gstin" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
              <input placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
              <input placeholder="City" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
              <input placeholder="Partner Type" value={form.partner_type} onChange={e => setForm({ ...form, partner_type: e.target.value })} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
              <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
                <option value="">Relationship Manager</option>{execs.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="b2b-status" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY); }} className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700">Cancel</button>
              <button onClick={save} data-testid="b2b-save" className="px-4 py-2 bg-primary text-white rounded-lg">Save</button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDetail(null)}>
          <div className="clay-card p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto space-y-4" onClick={e => e.stopPropagation()} data-testid="b2b-detail">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-medium">{detail.company_name}</h3>
              <div className="flex items-center gap-2">
                {canManage && <button onClick={() => openEdit(detail)} data-testid="b2b-edit" className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg">Edit</button>}
                <button onClick={() => setDetail(null)} className="text-muted-foreground">Close</button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-muted-foreground text-xs">Contact</div>{detail.contact_person || '\u2014'}</div>
              <div><div className="text-muted-foreground text-xs">Mobile</div>{detail.mobile}</div>
              <div><div className="text-muted-foreground text-xs">Alternate Mobile</div>{detail.alternate_mobile || '\u2014'}</div>
              <div><div className="text-muted-foreground text-xs">GSTIN</div>{detail.gstin || '\u2014'}</div>
              <div><div className="text-muted-foreground text-xs">Email</div>{detail.email || '\u2014'}</div>
              <div><div className="text-muted-foreground text-xs">City</div>{detail.city || '\u2014'}</div>
              <div className="md:col-span-2"><div className="text-muted-foreground text-xs">Address</div>{detail.address || '\u2014'}</div>
              <div><div className="text-muted-foreground text-xs">Relationship Mgr</div>{detail.assigned_to_name || '\u2014'}</div>
              <div><div className="text-muted-foreground text-xs">Repeat Business</div>{detail.repeat_business ? 'Yes' : 'No'}</div>
              <div><div className="text-muted-foreground text-xs">Total Projects</div>{detail.total_projects}</div>
              <div><div className="text-muted-foreground text-xs">Total Business</div>{money(detail.total_business)}</div>
              <div><div className="text-muted-foreground text-xs">Received</div>{money(detail.received)}</div>
              <div><div className="text-muted-foreground text-xs">Outstanding</div><span className="text-orange-600">{money(detail.outstanding)}</span></div>
            </div>

            {/* Business Insight Charts */}
            {(() => {
              const src = chartRange === 'year' ? (detail.year_wise || {}) : (detail.month_wise || {});
              const trend = Object.keys(src).sort().map(k => ({ period: k, business: src[k] }));
              const svc = Object.keys(detail.service_wise || {}).map(k => ({ service: k, business: detail.service_wise[k] }));
              const hasTrend = trend.length > 0;
              const hasSvc = svc.length > 0;
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="b2b-charts">
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-sm">{chartRange === 'year' ? 'Year-wise' : 'Month-wise'} Business{detail.repeat_business ? ' (incl. repeat)' : ''}</h4>
                      <div className="flex gap-1">
                        <button onClick={() => setChartRange('month')} data-testid="b2b-chart-month" className={`text-xs px-2 py-0.5 rounded ${chartRange === 'month' ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>Month</button>
                        <button onClick={() => setChartRange('year')} data-testid="b2b-chart-year" className={`text-xs px-2 py-0.5 rounded ${chartRange === 'year' ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>Year</button>
                      </div>
                    </div>
                    {hasTrend ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="period" fontSize={10} /><YAxis fontSize={10} />
                          <Tooltip formatter={(v) => money(v)} />
                          <Bar dataKey="business" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p className="text-xs text-muted-foreground py-8 text-center">No trend data.</p>}
                  </div>
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                    <h4 className="font-medium text-sm mb-2">Service-wise Business</h4>
                    {hasSvc ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={svc} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis type="number" fontSize={10} /><YAxis type="category" dataKey="service" fontSize={10} width={80} />
                          <Tooltip formatter={(v) => money(v)} />
                          <Bar dataKey="business" fill="#3B82F6" radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p className="text-xs text-muted-foreground py-8 text-center">No service data.</p>}
                  </div>
                </div>
              );
            })()}
            <div>
              <h4 className="font-medium mb-2">Project History</h4>
              <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="text-left text-muted-foreground"><tr><th className="py-2">Serial</th><th>Service</th><th>Value</th><th>Received</th><th>Outstanding</th><th>Stage</th><th>Status</th></tr></thead>
                <tbody>{detail.projects.length === 0 ? <tr><td colSpan={7} className="py-3 text-muted-foreground">No projects yet.</td></tr> : detail.projects.map(pr => (
                  <tr key={pr.lead_id} className="border-t border-slate-100 dark:border-slate-800"><td className="py-2"><Link to={`/leads/${pr.lead_id}`} className="text-primary hover:underline">{pr.project_serial || '\u2014'}</Link></td><td>{pr.service}</td><td>{money(pr.project_value)}</td><td>{money(pr.received)}</td><td className="text-orange-600">{money(pr.outstanding)}</td><td>{pr.current_stage || '\u2014'}</td><td>{pr.status || '\u2014'}</td></tr>
                ))}</tbody></table></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default B2BPartnersPage;
